'use client';

import { useEffect, useState } from 'react';
import { CartItemsList } from '../cart/CartItemsList';
import DeliveryInformation from '../order/DeliveryInformation';
import DietaryRequirements from '../order/DietaryRequirements';
import { useCartState, useCartActions } from '@/core/application/providers/StateMachineProvider';
import { CartSummary } from '../cart/CartSummary';
import { Check, InfoIcon, Trash2 } from 'lucide-react';
import { useOrderStep } from '../order/useOrderStep';
import { FormProvider, useForm } from 'react-hook-form';
import { useOrder } from '../order/OrderContext';
import { OrderStep, OrderReadyToSubmit, DeliveryFormData } from '../order/order.types';
import { submitOrder as submitOrderAction } from '@/app/actions/submitOrder';
import OrderFeedbackModal from '../order/OrderFeedbackModal';
import { useRouter } from 'next/navigation';
import NextButton from '../NextButton';
import PreviousButton from '../PreviousButton';
import OrderMenuButton from '../order/OrderMenuButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { deliveryFormSchema } from '@/lib/validations';
import config from '@/lib/config';
import { AnimatePresence, motion } from 'framer-motion';

const STEPS: Record<string, OrderStep> = {
  CART: 'cart',
  DIETARY: 'dietary',
  DELIVERY: 'delivery',
  SUMMARY: 'summary',
  SUBMITTED: 'submitted',
};

// const navigationButtonLabel = {
//   [STEPS.CART]: { prev: '', next: 'Dietary Information' },
//   [STEPS.DIETARY]: { prev: 'Cart', next: 'Delivery' },
//   [STEPS.DELIVERY]: { prev: 'Dietary Information', next: 'Order Summary' },
//   [STEPS.SUMMARY]: { prev: 'Delivery', next: 'Submit your order' },
//   [STEPS.SUBMITTED]: { prev: 'Order Summary', next: '' },
// };
const navigationButtonLabel = {
  [STEPS.CART]: { prev: 'Our ', next: 'Continue' },
  [STEPS.DIETARY]: { prev: 'Cart', next: 'Continue' },
  [STEPS.DELIVERY]: { prev: 'Dietary Information', next: 'Continue' },
  [STEPS.SUMMARY]: { prev: 'Delivery', next: 'Submit your order' },
  [STEPS.SUBMITTED]: { prev: 'Order Summary', next: '' },
};

const stepLabels = ['Cart', 'Dietaries', 'Delivery', 'Summary'];
const stepKeys = [STEPS.CART, STEPS.DIETARY, STEPS.DELIVERY, STEPS.SUMMARY];

const Order = () => {
  const deliveryDataFormMethod = useForm<DeliveryFormData>({
    resolver: zodResolver(deliveryFormSchema),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      contactMethod: 'email',
      zipCode: '',
      city: '',
      state: '',
      deliveryTime: '',
    },
    delayError: 1000,
  });
  const dietaryDataFormMethod = useForm();
  const cart = useCartState();
  const cartActions = useCartActions();
  const order = useOrder();
  const router = useRouter();
  const { isFirstStep, isLastStep, step: currentStep, nextStep, prevStep: goToPreviousStep } = useOrderStep();
  const [showDeliveryExpandedNotice, setShowDeliveryExpandedNotice] = useState(false);

  // Modal states
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    status: 'loading' | 'success' | 'error';
    orderId?: string;
    errorMessage?: string;
    orderData?: OrderReadyToSubmit;
  }>({
    isOpen: false,
    status: 'loading',
  });

  // Loading state for the submit button
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCurrentStepIndex = () => stepKeys.indexOf(currentStep);

  const handleOrderSubmission = async () => {
    setIsSubmitting(true);
    const cleanOrderData = order.createOrderToSubmit();

    if (!cleanOrderData) {
      setModalState({
        isOpen: true,
        status: 'error',
        errorMessage: 'Order data is not ready for submission',
      });
      setIsSubmitting(false);
      return;
    }

    // Convert CreateOrderDto to OrderReadyToSubmit format for legacy server action
    // Include actual cart data with prices and attributes from the cart state
    const legacyOrderData = {
      cart: cart.items.map((item) => ({
        variant_id: item.variant_id,
        quantity: item.quantity,
        note: item.note || '',
        sku: item.sku || '',
        in_cart: {
          added_at: item.in_cart?.added_at || Date.now(),
          price: item.in_cart?.price || 0,
          selected_attributes: item.in_cart?.selected_attributes || [],
        },
      })),
      deliveryData: {
        fullName: cleanOrderData.deliveryData.fullName,
        phoneNumber: cleanOrderData.deliveryData.phoneNumber,
        email: cleanOrderData.deliveryData.email,
        contactMethod: cleanOrderData.deliveryData.contactMethod as 'email' | 'phone',
        streetAddress: cleanOrderData.deliveryData.streetAddress,
        city: cleanOrderData.deliveryData.city,
        state: cleanOrderData.deliveryData.state,
        zipCode: cleanOrderData.deliveryData.zipCode,
        deliveryDate: cleanOrderData.deliveryData.deliveryDate,
        deliveryTime: cleanOrderData.deliveryData.deliveryTime,
      },
      dietaryData: cleanOrderData.dietaryData,
    };

    setModalState({
      isOpen: true,
      status: 'loading',
      orderData: legacyOrderData,
    });

    try {
      const result = await submitOrderAction({
        authToken: null,
        data: {
          order: legacyOrderData,
        },
      });

      // Success
      setModalState({
        isOpen: true,
        status: 'success',
        orderId: result.id,
        orderData: legacyOrderData,
      });

      // Move to submitted step after a brief delay
      setTimeout(() => {
        nextStep();
      }, 1000);
    } catch (error) {
      // Error
      setModalState({
        isOpen: true,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'An unexpected error occurred',
        orderData: legacyOrderData,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleRetrySubmission = () => {
    handleCloseModal();
    handleOrderSubmission();
  };

  const handleGoToHome = () => {
    handleCloseModal();
    router.push('/');
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Remove the old alert-based success handling since we now use modal
  useEffect(() => {
    if (currentStep === STEPS.SUBMITTED) {
      // This will be handled by the modal success state
      localStorage.removeItem('cart');
      cartActions.clearCart();
      order.resetOrder();
    }
  }, [cartActions, currentStep, order]);

  const goToNextStep = async () => {
    switch (currentStep) {
      case STEPS.CART:
        nextStep();
        break;

      case STEPS.DIETARY: {
        // Save dietary form data before proceeding
        const dietaryFormData = dietaryDataFormMethod.getValues();
        order.setDietaryData(dietaryFormData);
        nextStep();
        break;
      }

      case STEPS.DELIVERY: {
        const isValid = await deliveryDataFormMethod.trigger(undefined, { shouldFocus: true });

        console.log('is-valid', isValid);
        if (isValid) {
          // Save delivery form data before proceeding
          const deliveryFormData = deliveryDataFormMethod.getValues();

          // Convert form data to the expected DeliveryInfoDto format
          const deliveryInfoDto = {
            fullName: deliveryFormData.fullName || '',
            phoneNumber: deliveryFormData.phoneNumber || '',
            email: deliveryFormData.email || '',
            contactMethod: deliveryFormData.contactMethod || 'email',
            streetAddress: deliveryFormData.streetAddress || '',
            city: deliveryFormData.city || '',
            state: deliveryFormData.state || '',
            zipCode: deliveryFormData.zipCode || '',
            deliveryDate: deliveryFormData.deliveryDate,
            deliveryTime: deliveryFormData.deliveryTime || '',
          };

          order.setDeliveryData(deliveryInfoDto);
          nextStep();
        }
        break;
      }

      case STEPS.SUMMARY:
        await handleOrderSubmission();
        break;

      default:
        nextStep();
        break;
    }
  };

  const next = navigationButtonLabel[currentStep]?.next || 'Next';
  const previous = navigationButtonLabel[currentStep]?.prev || 'Previous';

  return (
    <div className="min-h-screen page-gradient to-rose-50">
      <div className="py-8 mb-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <div className="mb-2">Complete</div>
            <div>
              Your <span className="text-orange-600">Order</span>
            </div>
          </h1>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between relative">
              {/* Progress line */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 mx-8"></div>
              <div
                className="absolute top-4 left-0 h-0.5 bg-orange-500 transition-all duration-500 mx-8"
                style={{ width: `${(getCurrentStepIndex() / (stepLabels.length - 1)) * (100 - 4)}%` }}></div>

              {stepLabels.map((label, index) => {
                const stepKey = stepKeys[index];
                const isActive = stepKey === currentStep;
                const isCompleted = getCurrentStepIndex() > index;

                return (
                  <div key={stepKey} className="flex flex-col items-center relative z-10">
                    <div
                      className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 mb-2
                      ${
                        isActive
                          ? 'bg-orange-500 text-white'
                          : isCompleted
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <span>{index + 1}</span>}
                    </div>
                    <span
                      className={`
                      text-sm font-medium
                      ${isActive ? 'text-orange-600' : isCompleted ? 'text-green-600' : 'text-gray-500'}
                    `}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {!cart.isLoading && (
          <motion.div
            className="flex items-start p-3 border border-gray-200 rounded-lg mb-4 bg-white overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}>
            <InfoIcon className="w-4 h-4 mr-3 text-orange-800 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-800 italic flex flex-col gap-2">
              <p>
                Heads up: Right now, We are only shipping to Rochester, New York. Orders to other locations are subject
                to restrictions, and product availability.{' '}
                <a
                  href="#"
                  onClick={() => setShowDeliveryExpandedNotice(!showDeliveryExpandedNotice)}
                  className="underline">
                  {showDeliveryExpandedNotice ? 'Show less' : 'Know more'}
                </a>
              </p>
              {showDeliveryExpandedNotice && (
                <AnimatePresence>
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3, delay: 0.1 }}>
                    While most of our products ship locally to Rochester, NY, we can deliver our delicious{' '}
                    <span className="font-medium">Fruit Jam</span> and <span className="font-medium">Kremas</span> to
                    customers
                    <br />
                    in {config.allowedStates.map((state) => state.name).join(', ')}.
                  </motion.p>
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}

        {/* Content Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-3 pt-6 pb-8">
            {/* Step Content */}
            {currentStep === STEPS.CART && (
              <>
                {!cart.isEmpty && (
                  <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-200">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Your Cart</h3>
                      <p className="text-sm text-gray-600">
                        {cart.itemCount} item{cart.itemCount !== 1 ? 's' : ''} in your cart
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to clear all items from your cart?')) {
                          cartActions.clearCart();
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 border hover:border-red-200 rounded-lg transition-colors"
                      title="Clear all items from cart">
                      <Trash2 className="w-4 h-4" />
                      Clear Cart
                    </button>
                  </div>
                )}
                <CartItemsList
                  items={cart.items}
                  isLoading={cart.isLoading}
                  hasError={cart.hasError}
                  isEmpty={cart.isEmpty}
                />
              </>
            )}
            {currentStep === STEPS.DIETARY && (
              <FormProvider {...dietaryDataFormMethod}>
                <DietaryRequirements />
              </FormProvider>
            )}
            {currentStep === STEPS.DELIVERY && (
              <FormProvider {...deliveryDataFormMethod}>
                <DeliveryInformation />
              </FormProvider>
            )}
            {currentStep === STEPS.SUMMARY && <CartSummary />}
          </div>
        </motion.div>

        {/* Navigation Buttons */}
        {cart.itemCount > 0 && (
          <div className="flex justify-between items-center">
            {isFirstStep ? (
              <OrderMenuButton />
            ) : (
              <PreviousButton goToPreviousStep={goToPreviousStep} isFirstStep={isFirstStep} previous={previous} />
            )}
            <NextButton
              goToNextStep={goToNextStep}
              disabled={isLastStep || isSubmitting}
              isSubmitting={isSubmitting}
              next={next}
            />
          </div>
        )}

        {/* Order Feedback Modal */}
        <OrderFeedbackModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          status={modalState.status}
          orderId={modalState.orderId}
          errorMessage={modalState.errorMessage}
          onRetry={handleRetrySubmission}
          onGoToHome={handleGoToHome}
          orderData={modalState.orderData}
        />
      </div>
    </div>
  );
};

export default Order;
